<template>
  <div class="bg-white padding-five-all">
    <h5 class="color-orange">{{ messages.FILTERS_TITLE }}</h5>

    <b-container fluid>
      <b-row class="padding-ten-tb align-items-end">
        <b-col
          v-for="slot in filterSlots"
          :key="slot"
          cols="12"
          :lg="filterColLg"
          class="mb-3 mb-lg-0"
        >
          <template v-if="slot === 'periodo'">
            <label class="text-grey">{{ messages.FILTER_CORTE_LABEL }}</label>
            <b-form-select
              v-if="!loadingCatalogs"
              :key="'periodo-' + periodoOptions.length"
              class="grey-input"
              :value="filters.idPeriodo"
              :options="periodoOptions"
              :disabled="loading"
              @input="updateFilter('idPeriodo', $event)"
            />
            <b-form-select
              v-else
              class="grey-input"
              disabled
              :value="filters.idPeriodo"
              :options="periodoLoadingOptions"
            />
          </template>
          <template v-else-if="slot === 'proveedor'">
            <label class="text-grey">{{
              messages.FILTER_PROVEEDOR_LABEL
            }}</label>
            <b-form-select
              v-if="!loadingCatalogs"
              class="grey-input"
              :value="filters.idProv"
              :options="proveedorOptions"
              :disabled="loading"
              @input="updateFilter('idProv', $event)"
            />
            <b-form-select
              v-else
              class="grey-input"
              disabled
              :value="0"
              :options="proveedorLoadingOptions"
            />
          </template>
        </b-col>
      </b-row>

      <b-row class="align-items-center">
        <b-col cols="12" class="text-center text-lg-left">
          <b-button
            class="bg-orange-btn mr-2"
            :disabled="loading || !filters.idPeriodo"
            @click="$emit('search')"
          >
            <font-awesome-icon icon="search" />
            {{ messages.SEARCH_BUTTON }}
          </b-button>

          <b-button
            class="bg-grey-btn hidden-icon mr-2"
            :disabled="loading"
            @click="$emit('clear')"
          >
            <font-awesome-icon icon="broom" />
            {{ messages.CLEAR_BUTTON }}
          </b-button>

          <b-button
            class="btn-orange hidden-icon mr-2"
            :disabled="loading || exporting || !filters.idPeriodo"
            @click="$emit('export')"
          >
            <font-awesome-icon icon="file-excel" />
            {{ messages.EXPORT_BUTTON }}
          </b-button>

          <b-button
            class="btn-orange hidden-icon mr-2"
            :disabled="loading || !filters.idPeriodo"
            @click="$emit('open-import')"
          >
            <font-awesome-icon icon="upload" />
            {{ messages.IMPORT_BUTTON }}
          </b-button>

          <b-button
            class="btn-orange hidden-icon"
            :disabled="loading"
            @click="$emit('open-upload-pdf')"
          >
            <font-awesome-icon icon="file-pdf" />
            {{ messages.UPLOAD_PDF_BUTTON || "Cargar PDFs OC" }}
          </b-button>
         
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import {
  fetchLiquidacionPeriodoSelectOptions,
  fetchProviderSelectOptionsByCentralized,
} from "@/services/service-catalog/service-catalog.api";
import {
  OPERACIONES_FILTER_SLOTS,
  OPERACIONES_MESSAGES,
  getDefaultPeriodoFilterOptions,
  getDefaultProveedorFilterOptions,
} from "@/services/operaciones/operaciones.constants";
import {
  areOperacionesFiltersEqual,
  getDefaultOperacionesFilters,
  normalizeOperacionesFilters,
} from "@/services/operaciones/operaciones.contract";

export default {
  name: "OperacionesFiltersPanel",

  props: {
    filters: { type: Object, required: true },
    loading: { type: Boolean, default: false },
    exporting: { type: Boolean, default: false },
  },

  data() {
    return {
      messages: OPERACIONES_MESSAGES,
      periodoOptions: getDefaultPeriodoFilterOptions(),
      loadingCatalogs: false,
      proveedorOptions: getDefaultProveedorFilterOptions(),
    };
  },

  computed: {
    filterSlots() {
      return OPERACIONES_FILTER_SLOTS;
    },

    filterColLg() {
      return 3;
    },
    proveedorLoadingOptions() {
      return [{ value: 0, text: "Cargando proveedores..." }];
    },
    periodoLoadingOptions() {
      const current = Number(this.filters.idPeriodo ?? 0);
      if (current > 0) {
        return [{ value: current, text: "Cargando cortes..." }];
      }
      return [{ value: 0, text: "Cargando cortes..." }];
    },
  },

  created() {
    this.loadCatalogOptions();
  },

  methods: {
    
    async loadCatalogOptions() {
      this.loadingCatalogs = true;

      const results = await Promise.allSettled([
        fetchLiquidacionPeriodoSelectOptions(),
        fetchProviderSelectOptionsByCentralized(),
      ]);

      const [periodoResult, proveedorResult] = results;

      if (periodoResult.status === "fulfilled") {
        this.periodoOptions = periodoResult.value;
        this.applyDefaultPeriodoIfNeeded();
      } else {
        console.error(periodoResult.reason);
        this.periodoOptions = getDefaultPeriodoFilterOptions();
      }

      if (proveedorResult.status === "fulfilled") {
        this.proveedorOptions = proveedorResult.value;
      } else {
        console.error(proveedorResult.reason);
        this.proveedorOptions = getDefaultProveedorFilterOptions();
      }

      this.loadingCatalogs = false;
    },

    applyDefaultPeriodoIfNeeded() {
      const currentPeriodo = Number(this.filters.idPeriodo ?? 0);
      if (currentPeriodo > 0) return;

      const defaults = getDefaultOperacionesFilters();
      const defaultPeriodo = Number(defaults.idPeriodo ?? 0);
      if (defaultPeriodo <= 0) return;

      const exists = this.periodoOptions.some(
        (option) => Number(option.value) === defaultPeriodo,
      );
      if (!exists) return;

      this.emitFiltersIfChanged({
        ...this.filters,
        idPeriodo: defaultPeriodo,
      });
    },

    emitFiltersIfChanged(nextFilters) {
      const normalized = normalizeOperacionesFilters(nextFilters);

      if (areOperacionesFiltersEqual(normalized, this.filters)) return;

      this.$emit("update:filters", normalized);
    },

    updateFilter(key, value) {
      if (value === null || value === undefined || value === "") return;

      const parsedValue = Number(value);
      const normalizedValue =
        key === "idPeriodo"
          ? Number.isFinite(parsedValue)
            ? parsedValue
            : 0
          : value;

      this.emitFiltersIfChanged({
        ...this.filters,
        [key]: normalizedValue,
      });
    },
  },
};
</script>
